// File: frontend/src/utils/apiHelpers.js

// Lazy load Capacitor
let Capacitor = null;
let capacitorChecked = false;

export const getCapacitor = () => {
  if (!capacitorChecked) {
    try {
      // Synchronous check if available
      capacitorChecked = true;
      // Try to access if already loaded (e.g., in native builds)
      if (typeof window !== 'undefined' && window.Capacitor) {
        Capacitor = window.Capacitor;
      } else {
        Capacitor = { isNativePlatform: () => false, getPlatform: () => 'web' };
      }
    } catch (e) {
      Capacitor = { isNativePlatform: () => false, getPlatform: () => 'web' };
    }
  }
  return Capacitor;
};

/**
 * Determines the appropriate API URL based on the environment
 * Priority: Electron > Capacitor > Env Var > localStorage (dev) > Smart Default
 */
export const getApiUrl = () => {
  // Priority 1: Electron app - always use production server
  if (typeof window !== 'undefined' && window.electronAPI) {
    console.log('Running in Electron, using production server');
    return 'https://backend.steadily.me';
  }

  // Priority 2: Capacitor native app - always use production server
  const cap = getCapacitor();
  if (cap && cap.isNativePlatform()) {
    console.log('Running in Capacitor native app, using production server');
    return 'https://backend.steadily.me';
  }

  // Priority 3: localStorage (only in development, for dev convenience)
  // We check this BEFORE env vars so the UI toggle can override .env files
  if (process.env.NODE_ENV !== 'production') {
    try {
      const saved = localStorage.getItem('dev_api_url');
      if (saved) {
        console.log('Using API URL from localStorage:', saved);
        return saved;
      }
    } catch (e) {
      // Storage unavailable — fall through to env var / defaults.
      // NOTE: cannot use logger here (logger imports getApiUrl → cycle).
      console.warn('[warn][apiHelpers] localStorage read failed for dev_api_url', e?.message);
    }
  }

  // Priority 4: Environment variable (production/deployment config)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Priority 5: Smart default based on environment
  if (process.env.NODE_ENV === 'production') {
    // If deployed on steadily.me domain, use backend.steadily.me
    if (window.location.hostname.includes('steadily.me')) {
      return 'https://backend.steadily.me';
    }
    // Otherwise, assume same-origin backend on port 5000
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  }

  // Development default (web browser only)
  return 'http://localhost:5000';
};

/**
 * Clean up localStorage override in production, Electron, or Capacitor
 */
export const cleanupDevApiUrl = () => {
  const isElectron = typeof window !== 'undefined' && window.electronAPI;
  const cap = getCapacitor();
  if (process.env.NODE_ENV === 'production' || (cap && cap.isNativePlatform()) || isElectron) {
    try { localStorage.removeItem('dev_api_url'); } catch {}
  }
};

/**
 * Check if running in a native app (Electron or Capacitor)
 * @returns {boolean} True if running in Electron or Capacitor
 */
export const isNativeApp = () => {
  // Check for Electron
  if (typeof window !== 'undefined' && window.electronAPI) {
    return true;
  }

  // Check for Capacitor
  const cap = getCapacitor();
  if (cap && cap.isNativePlatform()) {
    return true;
  }

  return false;
};
